import { TransactionStatus, TransactionType } from '@seapoint/server/enums';
import { Dot } from 'lucide-react';
import { CopyIcon } from '../../../../../design-system/copy-icon/copy-icon';
import { Inline } from '../../../../../design-system/inline/inline';
import { NameWithLogo } from '../../../../../design-system/name-with-logo/name-with-logo';
import { Stack } from '../../../../../design-system/stack/stack';
import { getTransactionTypeDescription } from '../../../../../utils/get-transaction-type-description';

type PaymentInformationProps = {
  type: TransactionType;
  status: string;
  reference: string;
  transactionId: string;
  cardNumber?: string;
  cardHolderName?: string;
  account: {
    name: string;
    currency: string;
    imageUrl: string;
  };
};

type InfoRowData = {
  label: string;
  value: string | React.ReactNode;
};

const InfoRow = ({ label, value }: InfoRowData) => (
  <Inline justify="between" className="w-full">
    <div className="text-sm font-thin leading-[22px] text-seapoint-gray-text">{label}</div>
    <div className="text-sm font-thin leading-[22px]">{value}</div>
  </Inline>
);

export const PaymentInformation = ({
  status,
  type,
  reference,
  transactionId,
  cardNumber,
  cardHolderName,
  account,
}: PaymentInformationProps) => {
  const isCardPayment =
    type === TransactionType.CARD_PAYMENT ||
    type === TransactionType.CARD_CHARGEBACK ||
    type === TransactionType.CARD_REFUND;

  const getPaymentRows = (): InfoRowData[] => {
    if (isCardPayment) {
      return [
        { label: 'Status', value: getStatusLabel(status) },
        {
          label: 'Transaction ID',
          value: <TransactionIdRow transactionId={transactionId} />,
        },
        { label: 'Payment Method', value: getTransactionTypeDescription(type) },
        ...(cardNumber ? [{ label: 'Card Number', value: formatCardNumber(cardNumber) }] : []),
        ...(cardHolderName
          ? [{ label: 'Card Holder Name', value: cardHolderName }]
          : [
              {
                label: 'From',
                value: (
                  <NameWithLogo name={`${account.name} (${account.currency})`} logo={account.imageUrl} logoSize="lg" />
                ),
              },
            ]),
      ];
    }

    return [
      { label: 'Status', value: getStatusLabel(status) },
      { label: 'Reference', value: <span className="truncate">{reference}</span> },
      {
        label: 'Transaction ID',
        value: <TransactionIdRow transactionId={transactionId} />,
      },
      { label: 'Payment Method', value: getTransactionTypeDescription(type) },
      {
        label: 'From',
        value: <NameWithLogo name={`${account.name} (${account.currency})`} logo={account.imageUrl} logoSize="lg" />,
      },
    ];
  };

  return (
    <Stack space={4}>
      <div className="text-base font-medium leading-[22px] tracking-[-0.02em]">Payment Information</div>
      {getPaymentRows().map((row, index) => (
        <InfoRow key={index} label={row.label} value={row.value} />
      ))}
    </Stack>
  );
};

const formatCardNumber = (cardNumber: string): string => {
  const lastFour = cardNumber.slice(-4);
  return `**** **** **** ${lastFour}`;
};

const getStatusLabel = (status: string) => {
  const statusConfig = {
    [TransactionStatus.FAILED]: {
      color: '#FF531D',
      label: 'Failed',
    },
    [TransactionStatus.BOOKED]: {
      color: '#82CC69',
      label: 'Completed',
    },
    [TransactionStatus.PENDING]: {
      color: '#86868B',
      label: 'Pending',
    },
  };

  const config = statusConfig[status as TransactionStatus] || statusConfig[TransactionStatus.PENDING];

  return (
    <Inline align="center" space={0}>
      <Dot color={config.color} size={24} strokeWidth={4} />
      <p className="text-base font-thin">{config.label}</p>
    </Inline>
  );
};

const TransactionIdRow = ({ transactionId }: { transactionId: string }) => {
  return (
    <Inline align="center" space={0}>
      <CopyIcon textToCopy={transactionId} />
      <p className="text-sm font-thin leading-[22px]">{transactionId}</p>
    </Inline>
  );
};
